import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Button,
  TextField,
  InputAdornment,
  Grid,
  Typography,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Card,
  CardContent,
  Divider,
  Stack,
  useMediaQuery
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  MoreVert as MoreVertIcon,
  CheckCircle as CompletedIcon,
  LocalTaxi as TaxiIcon,
  DirectionsCar as CarIcon,
  Person as DriverIcon,
  Schedule as ScheduledIcon,
  Cancel as CancelledIcon,
  AccessTime as RequestedIcon,
  DirectionsRun as InProgressIcon,
  Delete as DeleteIcon,
  Event as EventIcon,
  FilterList as FilterListIcon,
  FlashOn as FlashOnIcon,
  Warning as WarningIcon,
  Repeat as RecurringIcon,
  ContentCopy as ContentCopyIcon,
  Phone as PhoneIcon,
  Smartphone as SmartphoneIcon,
  Timer as TimerIcon,
  AirportShuttle as KombiIcon,
  ElectricRickshaw as MinibusIcon,
  LocalHospital as MedicalIcon
} from '@mui/icons-material';
import { styled, useTheme } from '@mui/material/styles';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { de } from 'date-fns/locale';

import {
  fetchRides,
  // OLD LISTENERS DEAKTIVIERT - AppDataManager übernimmt Datenladung
  // startRidesStatusListener,
  // stopRidesStatusListener,
  deleteRide,
  updateRideStatus
} from '../../redux/slices/ridesSlice';
import { isCentralOperationsRole, isCentralRole } from '../../redux/slices/authSlice';
import { formatVehicleWithCapabilities, migrateSpecialVehicleType } from '../../config/vehicleTypes';
import PageHeader from '../common/PageHeader';
import Loading from '../common/Loading';
import ErrorMessage from '../common/ErrorMessage';

const StyledTableRow = styled(TableRow)(({ theme, isOverdue }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: isOverdue ? 'rgba(255, 68, 68, 0.1)' : theme.palette.action.hover,
  },
  '&:hover': {
    backgroundColor: isOverdue ? 'rgba(255, 68, 68, 0.2)' : theme.palette.action.selected,
  },
  ...(isOverdue && {
    backgroundColor: 'rgba(255, 68, 68, 0.08)',
    borderLeft: `4px solid #ff4444`,
  }),
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

// Helper-Funktionen für StatusChip
const getIconForStatus = (status) => {
  switch (status) {
    case 'completed':
      return <CompletedIcon />;
    case 'in_progress':
      return <InProgressIcon />;
    case 'arrived':
      return <DriverIcon />;
    case 'accepted':
      return <DriverIcon />;
    case 'pre_accepted':
      return <DriverIcon />;
    case 'open':
      return <RequestedIcon />;
    case 'dispatcher_pending':
      return <RequestedIcon />;
    case 'cancelled':
      return <CancelledIcon />;
    case 'cancelled_by_driver':
      return <CancelledIcon />;
    case 'scheduled':
      return <ScheduledIcon />;
    case 'paused':
      return <WarningIcon />;
    case 'dispatch_exhausted':
      return <WarningIcon />;
    default:
      return <RequestedIcon />;
  }
};

const getColorForStatus = (status, theme) => {
  switch (status) {
    case 'completed':
      return theme.palette.success.main;
    case 'in_progress':
      return theme.palette.primary.main;
    case 'arrived':
      // Teal – klar abgegrenzt von success (completed) und info (accepted),
      // semantisch passend als Wartezustand beim Kunden.
      return '#00897b';
    case 'accepted':
      return theme.palette.info.main;
    case 'open':
      return theme.palette.warning.main;
    case 'dispatch_exhausted':
      return theme.palette.error.main;
    case 'dispatcher_pending':
      return theme.palette.warning.main;
    case 'cancelled':
      return theme.palette.error.main;
    case 'cancelled_by_driver':
      return theme.palette.error.dark;
    case 'scheduled':
      return theme.palette.secondary.main;
    case 'pre_accepted':
      return theme.palette.info.light;
    case 'paused':
      return theme.palette.warning.dark;
    default:
      return undefined;
  }
};

// Vereinfachte StatusChip ohne verschachtelte Arrow-Funktionen
const StatusChip = styled(Chip)(({ theme, status }) => ({
  color: status === 'default' ? 'inherit' : 'white',
  backgroundColor: getColorForStatus(status, theme),
  '& .MuiSvgIcon-root': {
    fontSize: '1rem',
  }
}));

// Füge einen neuen styled Badge hinzu
const StyledBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
  },
}));

// Statische Maps – einmalig erstellt, nie neu berechnet
const STATUS_LABEL_MAP = {
  'open': 'Angefragt',
  'accepted': 'Akzeptiert',
  'arrived': 'Angekommen',
  'in_progress': 'Zielfahrt',
  'completed': 'Abgeschlossen',
  'cancelled': 'Storniert',
  'cancelled_by_driver': 'Vom Fahrer storniert',
  'scheduled': 'Geplant',
  'pre_accepted': 'Vom Fahrer angenommen',
  'dispatcher_pending': 'Fahrer angefragt',
  'paused': 'Pausiert',
  'dispatch_exhausted': 'Nicht vermittelbar',
};

const isDispatchExhausted = (ride) => ride?.dispatchState === 'exhausted';

const getDisplayStatus = (ride) => {
  if (ride?.status === 'open' && isDispatchExhausted(ride)) {
    return 'dispatch_exhausted';
  }
  return ride?.status;
};

const getStatusDetailLabel = (ride) => {
  if (ride?.status === 'cancelled' && isDispatchExhausted(ride)) {
    return 'Nicht vermittelbar';
  }
  return null;
};

const DISPATCH_SOURCE_LABEL_MAP = {
  sector_queue: 'Sektor-Angebot',
  distance_fallback: 'Fallback-Angebot',
  distance_direct: 'Entfernungsangebot',
  predicted_sector_followup: 'Anschlusstour'
};

const OFFER_TYPE_LABEL_MAP = {
  immediate: 'Sofortangebot',
  auto_follow_up: 'Anschlussangebot'
};

const MEDICAL_PROOF_LABEL_MAP = {
  missing: 'Nachweis fehlt',
  incomplete: 'Unvollständig',
  complete: 'Nachweis vollständig',
  waived: 'Nicht möglich',
  problem: 'Problem',
  rejected: 'Beanstandet'
};

const MEDICAL_PROOF_TYPE_LABEL_MAP = {
  digital_signature: 'Signatur',
  paper_proof: 'Papier',
  facility_confirmation: 'Einrichtung',
  cannot_sign: 'Keine Unterschrift',
  no_show: 'No-Show',
  problem: 'Problem'
};

const MEDICAL_BILLING_LABEL_MAP = {
  not_ready: 'Nicht bereit',
  ready: 'Bereit',
  exported: 'Exportiert',
  submitted: 'Eingereicht',
  paid: 'Bezahlt',
  rejected: 'Beanstandet'
};

const getDriverDisplayName = (driver) =>
  driver?.name || driver?.user?.name || driver?.displayName || driver?.email || null;

const shortId = (value) => {
  if (!value || typeof value !== 'string') return null;
  return value.length > 10 ? `${value.substring(0, 10)}...` : value;
};

// Aktive Fahrten werden immer angezeigt – unabhängig vom gewählten Zeitraum,
// damit der Disponent laufende Einsätze nie aus dem Blick verliert.
// pre_accepted ist bewusst NICHT enthalten: eine vor Wochen pre-akzeptierte
// Vorbestellung soll in Kurzfiltern wie "heute + 12 h" nicht auftauchen.
// Überfällige bzw. unmittelbar bevorstehende pre_accepted-Fahrten werden
// separat über isImminentPreAccepted berücksichtigt.
const ACTIVE_STATUSES = new Set([
  'in_progress', 'arrived', 'accepted', 'open', 'dispatcher_pending'
]);

const COMPLETED_STATUSES = new Set(['completed', 'cancelled', 'cancelled_by_driver']);

// Nicht zugewiesene App-Vorbestellung:
// Vorbestellung, die aus der Kunden-App kommt und noch keinen Fahrer hat.
// Solche Fahrten sollen sichtbar bleiben, bis sie einem Fahrer zugewiesen wurden,
// auch wenn sie weiter als 24 h in der Zukunft liegen.
// Daueraufträge (isRecurring) werden hier ausgeschlossen, weil sie vom
// Disponenten erstellt wurden, nicht vom App-Kunden – selbst wenn das
// isPhoneCustomer-Flag bei Alt-Daten oder fehlerhaft erzeugten Instanzen
// nicht gesetzt ist.
const isUnassignedAppPreOrder = (ride) =>
  ride.orderType === 1 &&
  !ride.isPhoneCustomer &&
  !ride.isRecurring &&
  !ride.driver &&
  !ride.driverId &&
  (ride.status === 'scheduled' || ride.status === 'open' || ride.status === 'dispatcher_pending');

const isAutoAcceptedOpenPreOrder = (ride) =>
  ride.orderType === 1 &&
  ride.autoAccepted === true &&
  ride.status === 'scheduled' &&
  !ride.driver &&
  !ride.driverId &&
  !ride.isPhoneCustomer;

const toMs = (t) => {
  if (!t) return null;
  if (t.toDate) return t.toDate().getTime();
  if (t.seconds) return t.seconds * 1000;
  const ms = new Date(t).getTime();
  return Number.isNaN(ms) ? null : ms;
};

// Überfällige oder unmittelbar bevorstehende Vorbestellungen (≤ 5 min bis
// zur geplanten Startzeit oder bereits überfällig) bleiben sichtbar, auch
// wenn der gewählte Zeitraum sie sonst ausschließen würde – sie verlangen
// Aktion des Disponenten. Entspricht der Schwelle von Tier 1 in getRideTier.
const isImminentPreAccepted = (ride, nowMs) => {
  if (ride.status !== 'pre_accepted') return false;
  const scheduledMs = toMs(ride.scheduledTime || ride.scheduled_time);
  if (scheduledMs === null) return true;
  return scheduledMs - nowMs <= 5 * 60 * 1000;
};

// Referenzzeitpunkt einer Fahrt für Zeitraum-Filter:
// Für abgeschlossene/stornierte Fahrten der Abschlusszeitpunkt,
// sonst die geplante Zeit (Vorbestellung) bzw. der Erstellzeitpunkt.
const getRideReferenceMs = (ride) => {
  if (COMPLETED_STATUSES.has(ride.status)) {
    return toMs(ride.completedTimestamp || ride.completionTimestamp)
      ?? toMs(ride.timestamp || ride.request_time);
  }
  return toMs(ride.scheduledTime || ride.scheduled_time)
    ?? toMs(ride.timestamp || ride.request_time);
};

// Tier-basierte Priorität (kleiner = weiter oben).
// Flow einer Fahrt: open/dispatcher_pending → accepted → arrived → in_progress → completed.
// Vorbestellung: scheduled → pre_accepted → accepted → arrived → in_progress → completed.
// `scheduled` bedeutet immer "ohne Fahrer" – sobald ein Fahrer zugewiesen oder die
// Vorbestellung akzeptiert wird, wechselt der Status auf `pre_accepted`.
// Ordnung folgt der Disponenten-Dringlichkeit: zuerst was Aktion verlangt
// (überfällig, open), dann was beobachtet wird (accepted, laufende Fahrten),
// dann bereits disponierte Zukunft und zuletzt Ruhezustände.
const getRideTier = (ride, nowMs) => {
  const { status } = ride;
  const scheduledMs = toMs(ride.scheduledTime || ride.scheduled_time);

  if (status === 'pre_accepted' && scheduledMs !== null && scheduledMs - nowMs <= 5 * 60 * 1000) return 1;
  if (status === 'open' || status === 'dispatcher_pending') return 2;
  if (status === 'accepted') return 3;
  if (status === 'in_progress' || status === 'arrived') return 4;
  if (status === 'pre_accepted') return 5;
  if (status === 'scheduled' && scheduledMs !== null && scheduledMs - nowMs <= 24 * 60 * 60 * 1000) return 6;
  if (status === 'scheduled' && isUnassignedAppPreOrder(ride)) return 8;
  if (status === 'scheduled') return 7;
  if (status === 'paused') return 10;
  return 9;
};

// Sortierschlüssel innerhalb eines Tiers (aufsteigend vergleichen).
// Sofortbestellungs-Statuses (in_progress, arrived, accepted, open, dispatcher_pending)
// sortieren nach Eingangs-Zeitstempel (FIFO), damit der Disponent die Reihenfolge sieht.
// Vorbestellungs-Tiers sortieren nach geplanter Startzeit.
// Abschluss-Tier (10) negiert, damit die neuesten oben stehen.
const getRideSortKey = (ride, tier) => {
  const scheduled = toMs(ride.scheduledTime || ride.scheduled_time);
  const timestamp = toMs(ride.timestamp || ride.request_time) ?? 0;
  const completed = toMs(ride.completedTimestamp || ride.completionTimestamp);

  switch (tier) {
    case 1: return scheduled ?? Number.MAX_SAFE_INTEGER;        // pre_accepted überfällig
    case 2: return timestamp;                                   // open, dispatcher_pending (FIFO)
    case 3: return timestamp;                                   // accepted (FIFO)
    case 4: return timestamp;                                   // in_progress, arrived (FIFO)
    case 5: return scheduled ?? Number.MAX_SAFE_INTEGER;        // pre_accepted (Vorbestellung)
    case 6:
    case 7:
    case 8: return scheduled ?? Number.MAX_SAFE_INTEGER;        // scheduled-Varianten
    case 9: return -(completed ?? timestamp);                   // completed/cancelled (neueste oben)
    case 10:
    default: return scheduled ?? timestamp;                     // paused
  }
};

const startOfDayMs = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x.getTime();
};

const endOfDayMs = (d) => {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x.getTime();
};

const getTimeRangeBounds = (range, customStart, customEnd) => {
  if (range === 'all') return null;
  const now = new Date();

  switch (range) {
    case 'today_plus_12h':
      return { start: startOfDayMs(now), end: now.getTime() + 12 * 60 * 60 * 1000 };
    case 'next_24h':
      return { start: now.getTime(), end: now.getTime() + 24 * 60 * 60 * 1000 };
    case 'today':
      return { start: startOfDayMs(now), end: endOfDayMs(now) };
    case 'tomorrow': {
      const t = new Date(now);
      t.setDate(t.getDate() + 1);
      return { start: startOfDayMs(t), end: endOfDayMs(t) };
    }
    case 'this_week': {
      const day = now.getDay();
      const diffToMonday = day === 0 ? -6 : 1 - day;
      const monday = new Date(now);
      monday.setDate(now.getDate() + diffToMonday);
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      return { start: startOfDayMs(monday), end: endOfDayMs(sunday) };
    }
    case 'next_week': {
      const day = now.getDay();
      const diffToMonday = day === 0 ? -6 : 1 - day;
      const nextMonday = new Date(now);
      nextMonday.setDate(now.getDate() + diffToMonday + 7);
      const nextSunday = new Date(nextMonday);
      nextSunday.setDate(nextMonday.getDate() + 6);
      return { start: startOfDayMs(nextMonday), end: endOfDayMs(nextSunday) };
    }
    case 'this_month': {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      const end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
      return { start: start.getTime(), end: end.getTime() };
    }
    case 'custom':
      if (!customStart || !customEnd) return null;
      return { start: startOfDayMs(customStart), end: endOfDayMs(customEnd) };
    default:
      return null;
  }
};

const DATE_FORMATTER = new Intl.DateTimeFormat('de-DE', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

const RidesList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  // Verwende neues AppData System statt altes rides slice (memoized)
  const rides = useSelector(state => state.appData.rides);
  const drivers = useSelector(state => state.appData.drivers);
  const vehicles = useSelector(state => state.appData.vehicles);
  const isLoading = useSelector(state => state.appData.isLoading.rides);
  const error = useSelector(state => state.appData.error.rides);
  const currentUser = useSelector(state => state.auth.user);
  const canDeleteRides = isCentralRole(currentUser);
  const canEditRide = useCallback((ride) => (
    isCentralOperationsRole(currentUser) ||
    (currentUser?.operatorId && ride?.createdByOperatorId === currentUser.operatorId)
  ), [currentUser]);
  
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [orderTypeFilter, setOrderTypeFilter] = useState('all');
  const [rideTypeFilter, setRideTypeFilter] = useState('all');
  const [medicalLinkFilter, setMedicalLinkFilter] = useState('all');
  const [medicalProofFilter, setMedicalProofFilter] = useState('all');
  const [medicalBillingFilter, setMedicalBillingFilter] = useState('all');
  const [timeRange, setTimeRange] = useState('today_plus_12h');
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [customStart, setCustomStart] = useState(null);
  const [customEnd, setCustomEnd] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuRide, setMenuRide] = useState(null);
  
  const [dataLoaded, setDataLoaded] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [rideToDelete, setRideToDelete] = useState(null);

  // Echtzeit-Wartezeiten aus Firebase
  const [waitTimes, setWaitTimes] = useState(null);
  const waitTimeUnsubRef = useRef(null);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'waitTimes', 'current'), (snap) => {
      if (snap.exists()) {
        const d = snap.data();
        setWaitTimes({
          general: d.general || '–',
          grossraum: d.grossraum || '–',
          kleinbus7: d.kleinbus7 || '–',
          kleinbus8: d.kleinbus8 || '–',
        });
      }
    });
    waitTimeUnsubRef.current = unsub;
    return () => unsub();
  }, []);

  // Lookup: driverId → Fahrzeugdaten (Fallback wenn driverModel im Ride-Dokument fehlt)
  const vehicleByDriverId = useMemo(() => {
    if (!vehicles || vehicles.length === 0) return {};
    const map = {};
    vehicles.forEach(v => {
      const dId = v.driverId || v.current_driver?.id;
      if (dId) {
        map[dId] = { model: v.model, licensePlate: v.licensePlate, vehicleNumber: v.vehicleNumber };
      }
    });
    return map;
  }, [vehicles]);

  const driverById = useMemo(() => {
    if (!drivers || drivers.length === 0) return {};
    return drivers.reduce((map, driver) => {
      if (driver.id) {
        map[driver.id] = driver;
      }
      return map;
    }, {});
  }, [drivers]);

  // Ermittle welche Fahrzeugtypen tatsächlich existieren
  const existingVehicleTypes = useMemo(() => {
    if (!vehicles || vehicles.length === 0) return new Set([1]); // PKW als Minimum
    const types = new Set();
    vehicles.forEach(v => {
      const vType = Number(v.vehicleType || v.vehicle_type || 1);
      types.add(vType);
    });
    return types;
  }, [vehicles]);

  useEffect(() => {
    const loadRides = async () => {
      try {
        // Alle Fahrten laden (inkl. abgeschlossene und stornierte)
        await dispatch(fetchRides({
          status: ['open', 'accepted', 'in_progress', 'arrived', 'scheduled', 'pre_accepted', 'completed', 'cancelled', 'cancelled_by_driver', 'paused']
        }));
        setDataLoaded(true);
      } catch (error) {
        console.error("RidesList: Fehler beim Laden der Fahrtdaten:", error);
        setDataLoaded(false);
      }
    };

    if (!dataLoaded) {
      loadRides();
    }
  }, [dispatch, dataLoaded]);

  const handleChangePage = useCallback((event, newPage) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const handleSearch = useCallback((event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  }, []);

  const handleStatusFilterChange = useCallback((event) => {
    setStatusFilter(event.target.value);
    setPage(0);
  }, []);

  const handleOrderTypeFilterChange = useCallback((event) => {
    setOrderTypeFilter(event.target.value);
    setPage(0);
  }, []);

  const handleRideTypeFilterChange = useCallback((event) => {
    setRideTypeFilter(event.target.value);
    setPage(0);
  }, []);

  const handleMedicalLinkFilterChange = useCallback((event) => {
    setMedicalLinkFilter(event.target.value);
    setPage(0);
  }, []);

  const handleMedicalProofFilterChange = useCallback((event) => {
    setMedicalProofFilter(event.target.value);
    setPage(0);
  }, []);

  const handleMedicalBillingFilterChange = useCallback((event) => {
    setMedicalBillingFilter(event.target.value);
    setPage(0);
  }, []);

  const handleTimeRangeChange = useCallback((event) => {
    setTimeRange(event.target.value);
    setPage(0);
  }, []);

  const handleCustomStartChange = useCallback((value) => {
    setCustomStart(value);
    setPage(0);
  }, []);

  const handleCustomEndChange = useCallback((value) => {
    setCustomEnd(value);
    setPage(0);
  }, []);

  const handleCloseMenu = useCallback(() => {
    setMenuAnchor(null);
    setMenuRide(null);
  }, []);

  const handleOpenMenu = useCallback((event, ride) => {
    setMenuAnchor(event.currentTarget);
    setMenuRide(ride);
  }, []);

  const handleDeleteClick = useCallback((ride) => {
    setRideToDelete(ride);
    setDeleteDialogOpen(true);
    setMenuAnchor(null);
    setMenuRide(null);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    try {
      await dispatch(deleteRide(rideToDelete.id)).unwrap();
      setDeleteDialogOpen(false);
      setRideToDelete(null);
    } catch (error) {
      console.error('Fehler beim Löschen der Fahrt:', error);
    }
  }, [dispatch, rideToDelete]);

  const handleCancelRide = useCallback(async (ride) => {
    if (!ride) return;
    try {
      await dispatch(updateRideStatus({
        rideId: ride.id,
        status: 'cancelled',
        additionalData: {
          cancelledBy: currentUser?.uid || null,
          cancelledByRole: currentUser?.role || null
        }
      })).unwrap();
    } catch (error) {
      console.error('Fehler beim Stornieren der Fahrt:', error);
    } finally {
      handleCloseMenu();
    }
  }, [currentUser, dispatch, handleCloseMenu]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteDialogOpen(false);
    setRideToDelete(null);
  }, []);
  
  const translateStatus = useCallback((status) => STATUS_LABEL_MAP[status] || status, []);

  const formatDate = useCallback((dateInput) => {
    if (!dateInput) return '-';
    try {
      let date;
      if (dateInput.toDate) {
        date = dateInput.toDate();
      } else if (dateInput.seconds) {
        date = new Date(dateInput.seconds * 1000);
      } else {
        date = new Date(dateInput);
      }
      if (isNaN(date.getTime())) return '-';
      return DATE_FORMATTER.format(date);
    } catch (error) {
      console.error('Fehler beim Formatieren des Datums:', error, dateInput);
      return '-';
    }
  }, []);

  const formatCurrency = useCallback((value) => {
    const amount = value?.estimatedEarningEur !== undefined ? value.estimatedEarningEur :
                  (value !== undefined ? value : null);
    if (amount === null || amount === undefined) return '-';
    return `${parseFloat(amount).toFixed(2)} €`;
  }, []);

  const formatDistance = useCallback((value) => {
    const timeOrDistance = value?.estimatedTimeMinutes !== undefined ? value.estimatedTimeMinutes :
                          (value !== undefined ? value : null);
    if (timeOrDistance === null || timeOrDistance === undefined) return '-';
    if (value?.estimatedTimeMinutes !== undefined) {
      return `${parseInt(timeOrDistance)} Min.`;
    }
    return `${parseFloat(timeOrDistance).toFixed(1)} km`;
  }, []);

  const renderMedicalTransportInfo = useCallback((ride) => {
    if (!ride.medicalTransportOrderId) {
      if (ride.isKrankenfahrt) {
        return (
          <Chip
            size="small"
            label="Ohne Verordnung"
            color="warning"
            variant="outlined"
          />
        );
      }
      return (
        <Typography variant="body2" color="textSecondary">
          -
        </Typography>
      );
    }

    const snapshot = ride.medicalTransportSnapshot || {};
    const proofStatus = ride.medicalTransportProofStatus || 'missing';
    const billingStatus = ride.medicalTransportBillingStatus || 'not_ready';
    const proofType = ride.medicalTransportProofType || '';
    const hasSignature = Array.isArray(ride.medicalTransportSignaturePoints) && ride.medicalTransportSignaturePoints.length > 0;
    const proofColor = proofStatus === 'complete'
      ? 'success'
      : proofStatus === 'problem' || proofStatus === 'rejected'
        ? 'error'
        : proofStatus === 'waived'
          ? 'info'
          : 'warning';
    const billingColor = billingStatus === 'ready' || billingStatus === 'paid'
      ? 'success'
      : billingStatus === 'rejected'
        ? 'error'
        : billingStatus === 'submitted' || billingStatus === 'exported'
          ? 'info'
          : 'default';

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'center' }}>
        <Chip
          icon={<MedicalIcon />}
          size="small"
          label={snapshot.patientName || 'Verordnung'}
          color="error"
          variant="outlined"
          sx={{ maxWidth: 180 }}
        />
        {snapshot.healthInsuranceName && (
          <Typography variant="caption" color="textSecondary" noWrap sx={{ maxWidth: 180 }}>
            {snapshot.healthInsuranceName}
          </Typography>
        )}
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'center' }}>
          <Chip
            size="small"
            label={MEDICAL_PROOF_LABEL_MAP[proofStatus] || proofStatus}
            color={proofColor}
            variant="outlined"
            sx={{ height: 20, fontSize: '0.7rem' }}
          />
          <Chip
            size="small"
            label={MEDICAL_BILLING_LABEL_MAP[billingStatus] || billingStatus}
            color={billingColor}
            variant="outlined"
            sx={{ height: 20, fontSize: '0.7rem' }}
          />
          {proofType && (
            <Chip
              size="small"
              label={MEDICAL_PROOF_TYPE_LABEL_MAP[proofType] || proofType}
              color={proofType === 'no_show' || proofType === 'problem' ? 'error' : 'info'}
              variant="outlined"
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
          )}
          {hasSignature && (
            <Chip
              size="small"
              label="Signatur vorhanden"
              color="success"
              variant="outlined"
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
          )}
        </Box>
      </Box>
    );
  }, []);

  const isRideOverdue = useCallback((ride) => {
    if (ride.status !== 'pre_accepted') return false;
    if (!ride.scheduledTime) return false;
    try {
      let scheduledDate;
      if (ride.scheduledTime.toDate) {
        scheduledDate = ride.scheduledTime.toDate();
      } else if (ride.scheduledTime.seconds) {
        scheduledDate = new Date(ride.scheduledTime.seconds * 1000);
      } else {
        scheduledDate = new Date(ride.scheduledTime);
      }
      const minutesUntilStart = Math.ceil((scheduledDate.getTime() - Date.now()) / (1000 * 60));
      return minutesUntilStart <= 5;
    } catch (error) {
      console.error('Fehler beim Prüfen der Überfälligkeit:', error);
      return false;
    }
  }, []);

  const getDelayInfo = useCallback((ride) => {
    if (ride.orderType !== 1 || !ride.scheduledTime) return null;
    try {
      let scheduledDate;
      if (ride.scheduledTime.toDate) {
        scheduledDate = ride.scheduledTime.toDate();
      } else if (ride.scheduledTime.seconds) {
        scheduledDate = new Date(ride.scheduledTime.seconds * 1000);
      } else {
        scheduledDate = new Date(ride.scheduledTime);
      }

      let actualStartDate = null;
      if (ride.startedAt) {
        if (ride.startedAt.toDate) {
          actualStartDate = ride.startedAt.toDate();
        } else if (ride.startedAt.seconds) {
          actualStartDate = new Date(ride.startedAt.seconds * 1000);
        } else {
          actualStartDate = new Date(ride.startedAt);
        }
      } else if (ride.passengerPickedUpTimestamp) {
        if (ride.passengerPickedUpTimestamp.toDate) {
          actualStartDate = ride.passengerPickedUpTimestamp.toDate();
        } else if (ride.passengerPickedUpTimestamp.seconds) {
          actualStartDate = new Date(ride.passengerPickedUpTimestamp.seconds * 1000);
        } else {
          actualStartDate = new Date(ride.passengerPickedUpTimestamp);
        }
      } else if (ride.status === 'pre_accepted') {
        const now = new Date();
        if (now > scheduledDate) {
          const delayMinutes = Math.floor((now.getTime() - scheduledDate.getTime()) / (1000 * 60));
          return { isDelayed: true, delayMinutes, status: 'pending' };
        }
        return null;
      }

      if (actualStartDate) {
        const delayMs = actualStartDate.getTime() - scheduledDate.getTime();
        const delayMinutes = Math.floor(delayMs / (1000 * 60));
        return {
          isDelayed: delayMinutes > 0,
          delayMinutes: Math.abs(delayMinutes),
          status: delayMinutes > 0 ? 'delayed' : (delayMinutes < 0 ? 'early' : 'ontime'),
          actualStartDate,
        };
      }
      return null;
    } catch (error) {
      console.error('Fehler beim Berechnen der Verspätung:', error);
      return null;
    }
  }, []);

  // Filter und Sort – wird nur neu berechnet wenn rides oder Filterkriterien sich ändern.
  // Echtzeit-Updates von Firebase (via AppDataManager → Redux) triggern automatisch eine
  // Neuberechnung, da `rides` als Dependency enthalten ist.
  const filteredData = useMemo(() => {
    const term = searchTerm.toLowerCase();
    const bounds = getTimeRangeBounds(timeRange, customStart, customEnd);
    const nowMs = Date.now();

    return rides.filter(ride => {
      const currentOfferDriver = ride.currentOfferDriverId
        ? driverById[ride.currentOfferDriverId]
        : null;
      const currentOfferDriverName = getDriverDisplayName(currentOfferDriver) || ride.currentOfferDriverName || '';

      const searchMatch = !searchTerm ||
        (ride.start || ride.pickup_address)?.toLowerCase().includes(term) ||
        (ride.destination || ride.destination_address)?.toLowerCase().includes(term) ||
        (ride.customerName || ride.customer?.name)?.toLowerCase().includes(term) ||
        (ride.medicalTransportSnapshot?.patientName || '')?.toLowerCase().includes(term) ||
        (ride.medicalTransportSnapshot?.healthInsuranceName || '')?.toLowerCase().includes(term) ||
        (ride.medicalTransportSnapshot?.approvalNumber || '')?.toLowerCase().includes(term) ||
        (ride.medicalTransportSnapshot?.prescriptionNumber || '')?.toLowerCase().includes(term) ||
        (ride.driverName || ride.driver?.user?.name)?.toLowerCase().includes(term) ||
        currentOfferDriverName.toLowerCase().includes(term) ||
        (ride.driverLicensePlate || ride.vehicle?.licensePlate)?.toLowerCase().includes(term) ||
        (ride.driverModel || ride.vehicle?.model)?.toLowerCase().includes(term);
      const statusMatch = statusFilter === 'all' || ride.status === statusFilter;
      const orderTypeMatch = orderTypeFilter === 'all' ||
        (orderTypeFilter === '1' && ride.orderType === 1) ||
        (orderTypeFilter === '2' && ride.orderType === 2);
      const rideTypeMatch = rideTypeFilter === 'all' ||
        (rideTypeFilter === 'krankenfahrt' && ride.isKrankenfahrt) ||
        (rideTypeFilter === 'rechnungsfahrt' && ride.isRechnungsfahrt);
      const medicalLinkMatch = medicalLinkFilter === 'all' ||
        (medicalLinkFilter === 'linked' && !!ride.medicalTransportOrderId) ||
        (medicalLinkFilter === 'unlinked_medical' && ride.isKrankenfahrt && !ride.medicalTransportOrderId) ||
        (medicalLinkFilter === 'none' && !ride.medicalTransportOrderId);
      const medicalProofMatch = medicalProofFilter === 'all' ||
        (medicalProofFilter === (ride.medicalTransportProofStatus || (ride.medicalTransportOrderId ? 'missing' : 'none')));
      const medicalBillingMatch = medicalBillingFilter === 'all' ||
        (medicalBillingFilter === (ride.medicalTransportBillingStatus || (ride.medicalTransportOrderId ? 'not_ready' : 'none')));

      // Zeitraum-Filter: aktive Fahrten, überfällige/bevorstehende Vorbestellungen
      // sowie nicht zugewiesene App-Vorbestellungen sind immer sichtbar –
      // unabhängig vom gewählten Zeitraum.
      let timeRangeMatch = true;
      if (
        bounds &&
        !ACTIVE_STATUSES.has(ride.status) &&
        !isUnassignedAppPreOrder(ride) &&
        !isImminentPreAccepted(ride, nowMs)
      ) {
        const refMs = getRideReferenceMs(ride);
        timeRangeMatch = refMs !== null && refMs >= bounds.start && refMs <= bounds.end;
      }

      return searchMatch &&
        statusMatch &&
        orderTypeMatch &&
        rideTypeMatch &&
        medicalLinkMatch &&
        medicalProofMatch &&
        medicalBillingMatch &&
        timeRangeMatch;
    }).sort((a, b) => {
      const tierA = getRideTier(a, nowMs);
      const tierB = getRideTier(b, nowMs);
      if (tierA !== tierB) return tierA - tierB;
      return getRideSortKey(a, tierA) - getRideSortKey(b, tierB);
    });
  }, [
    rides,
    driverById,
    searchTerm,
    statusFilter,
    orderTypeFilter,
    rideTypeFilter,
    medicalLinkFilter,
    medicalProofFilter,
    medicalBillingFilter,
    timeRange,
    customStart,
    customEnd
  ]);

  // Pagination – neu berechnet nur wenn filteredData, page oder rowsPerPage sich ändern
  const paginatedData = useMemo(
    () => filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredData, page, rowsPerPage]
  );
  
  if (isLoading) {
    return <Loading message="Fahrten werden geladen..." />;
  }
  
  if (error) {
    return (
      <ErrorMessage
        title="Fehler beim Laden der Fahrten"
        message={error}
        onRetry={() => dispatch(fetchRides())}
      />
    );
  }
  
  return (
    <Box sx={{ flexGrow: 1, p: { xs: 1.5, sm: 2, md: 3 } }}>
      <PageHeader
        title="Fahrtverwaltung"
        description="Übersicht und Verwaltung aller Fahrten"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Fahrten', path: '/fahrten' }
        ]}
        actionButton={{
          label: 'Fahrt hinzufügen',
          icon: <AddIcon />,
          path: '/fahrten/neu'
        }}
        secondaryButtons={[
          {
            label: 'Daueraufträge',
            icon: <RecurringIcon />,
            path: '/fahrten/dauerauftraege'
          },
          {
            label: 'Krankenfahrten',
            icon: <MedicalIcon />,
            path: '/krankenfahrten'
          },
          {
            label: 'Abrechnung',
            icon: <MedicalIcon />,
            path: '/krankenfahrten/abrechnung'
          }
        ]}
      />

      {/* Wartezeit-Anzeige */}
      {waitTimes && (
        <Paper
          elevation={0}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 2,
            py: 1,
            mb: 2,
            borderRadius: 2,
            bgcolor: 'grey.100',
            border: '1px solid',
            borderColor: 'grey.300',
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mr: 0.5 }}>
            <TimerIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary" fontWeight="medium">
              Aktuelle Wartezeit:
            </Typography>
          </Box>
          <Chip
            icon={<CarIcon />}
            label={`PKW ${waitTimes.general}`}
            size="small"
            variant="outlined"
            sx={{ borderColor: 'warning.main', color: 'warning.dark', '& .MuiChip-icon': { color: 'warning.dark' } }}
          />
          {existingVehicleTypes.has(2) && (
            <Chip
              icon={<KombiIcon />}
              label={`Großraum ${waitTimes.grossraum}`}
              size="small"
              variant="outlined"
              sx={{ borderColor: 'info.main', color: 'info.dark', '& .MuiChip-icon': { color: 'info.dark' } }}
            />
          )}
          {existingVehicleTypes.has(3) && (
            <Chip
              icon={<MinibusIcon />}
              label={`Kleinbus 7 ${waitTimes.kleinbus7}`}
              size="small"
              variant="outlined"
              sx={{ borderColor: 'success.main', color: 'success.dark', '& .MuiChip-icon': { color: 'success.dark' } }}
            />
          )}
          {existingVehicleTypes.has(4) && (
            <Chip
              icon={<MinibusIcon />}
              label={`Kleinbus 8 ${waitTimes.kleinbus8}`}
              size="small"
              variant="outlined"
              sx={{ borderColor: 'secondary.main', color: 'secondary.dark', '& .MuiChip-icon': { color: 'secondary.dark' } }}
            />
          )}
        </Paper>
      )}

      {/* Filter und Suche */}
      <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: { xs: 2, md: 3 } }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField
              fullWidth
              variant="outlined"
              label="Suche (Adresse, Kunde, Fahrer, Kennzeichen)"
              value={searchTerm}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid size={{ xs: 12 }} sx={{ display: { xs: 'block', sm: 'none' } }}>
            <Stack direction="row" spacing={1} sx={{ overflowX: 'auto', pb: 0.5 }}>
              <Chip
                clickable
                label="Heute + 12h"
                color={timeRange === 'today_plus_12h' ? 'primary' : 'default'}
                variant={timeRange === 'today_plus_12h' ? 'filled' : 'outlined'}
                onClick={() => { setTimeRange('today_plus_12h'); setPage(0); }}
              />
              <Chip
                clickable
                label="Offen"
                color={statusFilter === 'open' ? 'warning' : 'default'}
                variant={statusFilter === 'open' ? 'filled' : 'outlined'}
                onClick={() => { setStatusFilter(statusFilter === 'open' ? 'all' : 'open'); setPage(0); }}
              />
              <Chip
                clickable
                label="Geplant"
                color={statusFilter === 'scheduled' ? 'secondary' : 'default'}
                variant={statusFilter === 'scheduled' ? 'filled' : 'outlined'}
                onClick={() => { setStatusFilter(statusFilter === 'scheduled' ? 'all' : 'scheduled'); setPage(0); }}
              />
              <Chip
                clickable
                label="Vorbest."
                color={orderTypeFilter === '1' ? 'secondary' : 'default'}
                variant={orderTypeFilter === '1' ? 'filled' : 'outlined'}
                onClick={() => { setOrderTypeFilter(orderTypeFilter === '1' ? 'all' : '1'); setPage(0); }}
              />
              <Chip
                clickable
                label="Krankenfahrt"
                color={rideTypeFilter === 'krankenfahrt' ? 'error' : 'default'}
                variant={rideTypeFilter === 'krankenfahrt' ? 'filled' : 'outlined'}
                onClick={() => { setRideTypeFilter(rideTypeFilter === 'krankenfahrt' ? 'all' : 'krankenfahrt'); setPage(0); }}
              />
            </Stack>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {filteredData.length} Fahrten
              </Typography>
              <Button
                size="small"
                startIcon={<FilterListIcon />}
                onClick={() => setMobileFiltersOpen(open => !open)}
              >
                {mobileFiltersOpen ? 'Filter schließen' : 'Mehr Filter'}
              </Button>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 2 }} sx={{ display: { xs: mobileFiltersOpen ? 'block' : 'none', sm: 'block' } }}>
            <FormControl fullWidth>
              <InputLabel id="order-type-filter-label">Bestellart</InputLabel>
              <Select
                labelId="order-type-filter-label"
                id="order-type-filter"
                value={orderTypeFilter}
                label="Bestellart"
                onChange={handleOrderTypeFilterChange}
              >
                <MenuItem value="all">Alle</MenuItem>
                <MenuItem value="1">Vorbestellungen</MenuItem>
                <MenuItem value="2">Sofortbestellungen</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 2 }} sx={{ display: { xs: mobileFiltersOpen ? 'block' : 'none', sm: 'block' } }}>
            <FormControl fullWidth>
              <InputLabel id="status-filter-label">Status</InputLabel>
              <Select
                labelId="status-filter-label"
                id="status-filter"
                value={statusFilter}
                label="Status"
                onChange={handleStatusFilterChange}
              >
                <MenuItem value="all">Alle Status</MenuItem>
                <MenuItem value="open">Angefragt</MenuItem>
                <MenuItem value="accepted">Akzeptiert</MenuItem>
                <MenuItem value="pre_accepted">Vom Fahrer angenommen</MenuItem>
                <MenuItem value="arrived">Angekommen</MenuItem>
                <MenuItem value="in_progress">Zielfahrt</MenuItem>
                <MenuItem value="completed">Abgeschlossen</MenuItem>
                <MenuItem value="cancelled">Storniert</MenuItem>
                <MenuItem value="cancelled_by_driver">Vom Fahrer storniert</MenuItem>
                <MenuItem value="scheduled">Geplant</MenuItem>
                <MenuItem value="paused">Pausiert</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 2 }} sx={{ display: { xs: mobileFiltersOpen ? 'block' : 'none', sm: 'block' } }}>
            <FormControl fullWidth>
              <InputLabel id="ride-type-filter-label">Fahrttyp</InputLabel>
              <Select
                labelId="ride-type-filter-label"
                id="ride-type-filter"
                value={rideTypeFilter}
                label="Fahrttyp"
                onChange={handleRideTypeFilterChange}
              >
                <MenuItem value="all">Alle</MenuItem>
                <MenuItem value="krankenfahrt">Krankenfahrt</MenuItem>
                <MenuItem value="rechnungsfahrt">Rechnungsfahrt</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 2 }} sx={{ display: { xs: mobileFiltersOpen ? 'block' : 'none', sm: 'block' } }}>
            <FormControl fullWidth>
              <InputLabel id="medical-link-filter-label">Verordnung</InputLabel>
              <Select
                labelId="medical-link-filter-label"
                id="medical-link-filter"
                value={medicalLinkFilter}
                label="Verordnung"
                onChange={handleMedicalLinkFilterChange}
              >
                <MenuItem value="all">Alle</MenuItem>
                <MenuItem value="linked">Mit Verordnung</MenuItem>
                <MenuItem value="unlinked_medical">Krankenfahrt ohne Verordnung</MenuItem>
                <MenuItem value="none">Ohne Verordnung</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 2 }} sx={{ display: { xs: mobileFiltersOpen ? 'block' : 'none', sm: 'block' } }}>
            <FormControl fullWidth>
              <InputLabel id="medical-proof-filter-label">Nachweis</InputLabel>
              <Select
                labelId="medical-proof-filter-label"
                id="medical-proof-filter"
                value={medicalProofFilter}
                label="Nachweis"
                onChange={handleMedicalProofFilterChange}
              >
                <MenuItem value="all">Alle</MenuItem>
                <MenuItem value="missing">Fehlt</MenuItem>
                <MenuItem value="incomplete">Unvollständig</MenuItem>
                <MenuItem value="complete">Vollständig</MenuItem>
                <MenuItem value="waived">Nicht möglich</MenuItem>
                <MenuItem value="problem">Problem</MenuItem>
                <MenuItem value="rejected">Beanstandet</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 2 }} sx={{ display: { xs: mobileFiltersOpen ? 'block' : 'none', sm: 'block' } }}>
            <FormControl fullWidth>
              <InputLabel id="medical-billing-filter-label">Abrechnung</InputLabel>
              <Select
                labelId="medical-billing-filter-label"
                id="medical-billing-filter"
                value={medicalBillingFilter}
                label="Abrechnung"
                onChange={handleMedicalBillingFilterChange}
              >
                <MenuItem value="all">Alle</MenuItem>
                <MenuItem value="not_ready">Nicht bereit</MenuItem>
                <MenuItem value="ready">Bereit</MenuItem>
                <MenuItem value="exported">Exportiert</MenuItem>
                <MenuItem value="submitted">Eingereicht</MenuItem>
                <MenuItem value="paid">Bezahlt</MenuItem>
                <MenuItem value="rejected">Beanstandet</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }} sx={{ display: { xs: mobileFiltersOpen ? 'block' : 'none', sm: 'block' } }}>
            <FormControl fullWidth>
              <InputLabel id="time-range-label">Zeitraum</InputLabel>
              <Select
                labelId="time-range-label"
                id="time-range"
                value={timeRange}
                label="Zeitraum"
                onChange={handleTimeRangeChange}
              >
                <MenuItem value="today_plus_12h">Heute + nächste 12h</MenuItem>
                <MenuItem value="next_24h">Nächste 24 Stunden</MenuItem>
                <MenuItem value="today">Heute</MenuItem>
                <MenuItem value="tomorrow">Morgen</MenuItem>
                <MenuItem value="this_week">Diese Woche</MenuItem>
                <MenuItem value="next_week">Nächste Woche</MenuItem>
                <MenuItem value="this_month">Dieser Monat</MenuItem>
                <MenuItem value="custom">Individueller Zeitraum…</MenuItem>
                <MenuItem value="all">Alle Zeiträume</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 2 }} sx={{ display: { xs: 'none', sm: 'block' } }}>
            <Typography variant="body2" color="textSecondary">
              {filteredData.length} Fahrten gefunden
            </Typography>
          </Grid>
          {timeRange === 'custom' && (
            <Grid size={{ xs: 12 }} sx={{ display: { xs: mobileFiltersOpen ? 'block' : 'none', sm: 'block' } }}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <DatePicker
                    label="Von"
                    value={customStart}
                    onChange={handleCustomStartChange}
                    slotProps={{ textField: { size: 'small' } }}
                  />
                  <DatePicker
                    label="Bis"
                    value={customEnd}
                    onChange={handleCustomEndChange}
                    slotProps={{ textField: { size: 'small' } }}
                  />
                </Box>
              </LocalizationProvider>
            </Grid>
          )}
        </Grid>
      </Paper>
      
      {/* Mobile Fahrtkarten */}
      <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 1.5 }}>
        {paginatedData.length > 0 ? (
          paginatedData.map((ride) => {
            const isOverdue = isRideOverdue(ride);
            const delayInfo = getDelayInfo(ride);
            const driverName = ride.driverName || getDriverDisplayName(ride.driver);
            const vModel = ride.driverModel || ride.vehicle?.model || vehicleByDriverId[ride.driverId]?.model;
            const vPlate = ride.driverLicensePlate || ride.vehicle?.licensePlate || vehicleByDriverId[ride.driverId]?.licensePlate;
            const vNumber = ride.vehicle?.vehicleNumber || vehicleByDriverId[ride.driverId]?.vehicleNumber;
            const displayStatus = getDisplayStatus(ride);
            const statusDetailLabel = getStatusDetailLabel(ride);
            const canAssign = ride.status === 'open' || ride.status === 'scheduled';

            return (
              <Card
                key={ride.id}
                variant="outlined"
                sx={{
                  borderLeft: isOverdue ? '4px solid #ff4444' : '4px solid transparent',
                  bgcolor: isOverdue ? 'rgba(255, 68, 68, 0.04)' : 'background.paper',
                }}
              >
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'flex-start' }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(ride.timestamp || ride.request_time)}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mt: 0.5 }}>
                        <StatusChip
                          label={translateStatus(displayStatus)}
                          status={displayStatus}
                          size="small"
                          icon={getIconForStatus(displayStatus)}
                          sx={isOverdue ? { backgroundColor: '#ff4444 !important' } : {}}
                        />
                        {statusDetailLabel && (
                          <Typography
                            variant="caption"
                            color="error.main"
                            fontWeight="bold"
                            display="block"
                            sx={{ width: '100%' }}
                          >
                            {statusDetailLabel}
                          </Typography>
                        )}
                        <Chip
                          icon={(ride.orderType === 1 || ride.convertedToOpenRide) ? <EventIcon fontSize="small" /> : <FlashOnIcon fontSize="small" />}
                          label={(ride.orderType === 1 || ride.convertedToOpenRide) ? 'Vorbestellung' : 'Sofort'}
                          size="small"
                          color={(ride.orderType === 1 || ride.convertedToOpenRide) ? 'secondary' : 'primary'}
                          variant="outlined"
                        />
                        {ride.isKrankenfahrt && (
                          <Chip size="small" label="Krankenfahrt" color="error" variant="outlined" />
                        )}
                      </Box>
                    </Box>
                    <IconButton
                      aria-label="Aktionen"
                      aria-controls={`ride-menu-${ride.id}`}
                      aria-haspopup="true"
                      onClick={(e) => handleOpenMenu(e, ride)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </Box>

                  {isOverdue && (
                    <Typography variant="caption" sx={{ color: '#ff4444', fontWeight: 'bold', display: 'block', mt: 1 }}>
                      Überfällig oder unmittelbar fällig
                    </Typography>
                  )}

                  <Box sx={{ mt: 1.25, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
                      Von: {ride.start || ride.pickup_address || '-'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                      Nach: {ride.destination || ride.destination_address || 'Kein Ziel angegeben'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Kunde: {ride.customerName || ride.customer?.name || 'Unbekannt'}
                    </Typography>
                    {ride.notes && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontStyle: 'italic', overflowWrap: 'anywhere' }}
                      >
                        Notiz: {ride.notes}
                      </Typography>
                    )}
                  </Box>

                  <Divider sx={{ my: 1.25 }} />

                  <Grid container spacing={1}>
                    <Grid size={6}>
                      <Typography variant="caption" color="text.secondary">Zeit</Typography>
                      <Typography variant="body2">
                        {ride.orderType === 1 && ride.scheduledTime
                          ? formatDate(ride.scheduledTime)
                          : ride.estimatedTimeMinutes
                            ? `${ride.estimatedTimeMinutes} Min. geschätzt`
                            : 'Sofort'}
                      </Typography>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="caption" color="text.secondary">Fahrer</Typography>
                      <Typography variant="body2">
                        {driverName || (ride.currentOfferDriverId ? 'Angefragt' : 'Nicht zugewiesen')}
                      </Typography>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="caption" color="text.secondary">Fahrzeugtyp</Typography>
                      <Typography variant="body2">
                        {formatVehicleWithCapabilities(ride.vehicleType, ride.specialCapabilities || migrateSpecialVehicleType(ride))}
                      </Typography>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="caption" color="text.secondary">Fahrzeug</Typography>
                      {vModel || vPlate ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                          {vNumber && (
                            <Chip
                              label={vNumber}
                              size="small"
                              color="primary"
                              variant="filled"
                              sx={{ height: 20, fontWeight: 700, fontSize: '0.7rem', alignSelf: 'flex-start' }}
                            />
                          )}
                          <Typography variant="body2">
                            {vModel || ''}{vPlate ? ` (${vPlate})` : ''}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2">Nicht zugewiesen</Typography>
                      )}
                    </Grid>
                  </Grid>

                  {delayInfo && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 1, color: delayInfo.status === 'pending' ? '#ff4444' : 'text.secondary' }}>
                      {delayInfo.status === 'pending'
                        ? `${delayInfo.delayMinutes} Min. verspätet`
                        : delayInfo.status === 'delayed'
                          ? `${delayInfo.delayMinutes} Min. verspätet gestartet`
                          : delayInfo.status === 'early'
                            ? `${delayInfo.delayMinutes} Min. früher gestartet`
                            : 'Pünktlich gestartet'}
                    </Typography>
                  )}

                  <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
                    <Button
                      fullWidth
                      variant="outlined"
                      size="small"
                      component={Link}
                      to={`/fahrten/${ride.id}`}
                    >
                      Anzeigen
                    </Button>
                    {canAssign && (
                      <Button
                        fullWidth
                        variant="contained"
                        size="small"
                        component={Link}
                        to={`/fahrten/${ride.id}/zuweisen`}
                        state={{ from: '/fahrten' }}
                        startIcon={<TaxiIcon />}
                      >
                        Zuweisen
                      </Button>
                    )}
                  </Box>
                </CardContent>
              </Card>
            );
          })
        ) : (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body1">Keine Fahrten gefunden</Typography>
          </Paper>
        )}
        <TablePagination
          rowsPerPageOptions={isMobile ? [10, 25] : [10, 25, 50]}
          component="div"
          count={filteredData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Pro Seite:"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} von ${count}`}
          sx={{
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            '.MuiTablePagination-toolbar': {
              px: 1,
              flexWrap: 'wrap',
              justifyContent: 'center',
            },
          }}
        />
      </Box>

      {/* Desktop Fahrtentabelle */}
      <TableContainer component={Paper} sx={{ display: { xs: 'none', md: 'block' } }}>
        <Table sx={{ minWidth: 900 }} aria-label="Fahrtentabelle">
          <TableHead>
            <TableRow>
              <TableCell>Fahrt</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Verordnung</TableCell>
              <TableCell align="center">Fahrzeugtyp</TableCell>
              <TableCell align="center">Zeiten</TableCell>
              <TableCell align="center">Fahrer</TableCell>
              <TableCell align="center">Fahrzeug</TableCell>
              <TableCell align="right">Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.length > 0 ? (
              paginatedData.map((ride) => {
                const isOverdue = isRideOverdue(ride);
                const delayInfo = getDelayInfo(ride);
                return (
                  <StyledTableRow key={ride.id} isOverdue={isOverdue}>
                    <TableCell component="th" scope="row">
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption">
                          {formatDate(ride.timestamp || ride.request_time)}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <StyledBadge
                            badgeContent={ride.convertedToOpenRide ? "!" : 0}
                            color="error"
                            anchorOrigin={{
                              vertical: 'top',
                              horizontal: 'right',
                            }}
                          >
                            <Chip
                              icon={(ride.orderType === 1 || ride.convertedToOpenRide) ? <EventIcon fontSize="small" /> : <FlashOnIcon fontSize="small" />}
                              label={(ride.orderType === 1 || ride.convertedToOpenRide) ? "Vorbestellung" : "Sofortbestellung"}
                              size="small"
                              color={(ride.orderType === 1 || ride.convertedToOpenRide) ? "secondary" : "primary"}
                              variant="outlined"
                            />
                          </StyledBadge>
                          {(ride.isRecurring || ride.recurringOrderId) && (
                            <RecurringIcon
                              fontSize="small"
                              sx={{ color: 'primary.main' }}
                            />
                          )}
	                          {ride.isAnschlusstour && (
	                            <Chip
                              label="AT"
                              size="small"
                              color="success"
                              variant="outlined"
                              title="Anschlusstour"
	                              sx={{ height: 20, fontSize: '0.7rem' }}
	                            />
	                          )}
	                          {isAutoAcceptedOpenPreOrder(ride) && (
	                            <Chip
	                              label="Automatisch bestätigt"
	                              size="small"
	                              color="success"
	                              variant="outlined"
	                              sx={{ height: 20, fontSize: '0.7rem' }}
	                            />
	                          )}
	                          <Chip
                            icon={ride.isPhoneCustomer ? <PhoneIcon fontSize="small" /> : <SmartphoneIcon fontSize="small" />}
                            label={ride.isPhoneCustomer ? "Telefon" : "App"}
                            size="small"
                            variant="outlined"
                            sx={{
                              height: 20,
                              fontSize: '0.7rem',
                              borderColor: ride.isPhoneCustomer ? 'warning.main' : 'success.main',
                              color: ride.isPhoneCustomer ? 'warning.dark' : 'success.dark',
                              '& .MuiChip-icon': {
                                color: ride.isPhoneCustomer ? 'warning.dark' : 'success.dark',
                                fontSize: '0.8rem',
                              },
                            }}
                          />
                        </Box>
                      </Box>
                      <Typography variant="body2" color="textSecondary" noWrap sx={{ maxWidth: 250 }}>
                        Von: {ride.start || ride.pickup_address}
                      </Typography>
                      <Typography variant="body2" color="textSecondary" noWrap sx={{ maxWidth: 250 }}>
                        Nach: {ride.destination || ride.destination_address}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Kunde: {ride.customerName || ride.customer?.name || 'Unbekannt'}
                      </Typography>
                      {ride.notes && (
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          noWrap
                          sx={{ maxWidth: 250, fontStyle: 'italic' }}
                          title={ride.notes}
                        >
                          Notiz: {ride.notes}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ position: 'relative' }}>
                      {(() => {
                        const displayStatus = getDisplayStatus(ride);
                        const statusDetailLabel = getStatusDetailLabel(ride);
                        return (
                          <>
                            <StatusChip
                              label={translateStatus(displayStatus)}
                              status={displayStatus}
                              size="small"
                              icon={getIconForStatus(displayStatus)}
                              sx={isOverdue ? {
                                backgroundColor: '#ff4444 !important',
                                color: 'white !important',
                                animation: 'pulse 2s infinite',
                                '@keyframes pulse': {
                                  '0%': { transform: 'scale(1)' },
                                  '50%': { transform: 'scale(1.05)' },
                                  '100%': { transform: 'scale(1)' },
                                },
                              } : {}}
                            />
                            {statusDetailLabel && (
                              <Typography
                                variant="caption"
                                display="block"
                                color="error.main"
                                fontWeight="bold"
                                sx={{ mt: 0.5 }}
                              >
                                {statusDetailLabel}
                              </Typography>
                            )}
                          </>
                        );
                      })()}
                      {isOverdue && (
                        <Typography variant="caption" display="block" sx={{ 
                          mt: 0.5, 
                          color: '#ff4444', 
                          fontWeight: 'bold',
                          fontSize: '0.7rem'
                        }}>
                          ⚠️ ÜBERFÄLLIG
                        </Typography>
                      )}
                    </Box>
                    {(ride.scheduledTime || ride.orderType === 1) && (
                      <Typography variant="caption" display="block" sx={{ 
                        mt: 1,
                        color: isOverdue ? '#ff4444' : 'text.secondary',
                        fontWeight: isOverdue ? 'bold' : 'normal'
                      }}>
                        Geplant für: {formatDate(ride.scheduledTime || ride.scheduled_time)}
                      </Typography>
                    )}
                    {delayInfo && (
                      <Box sx={{ mt: 0.5, display: 'flex', justifyContent: 'center' }}>
                        {delayInfo.status === 'pending' && (
                          <Typography variant="caption" sx={{ 
                            color: '#ff4444', 
                            fontWeight: 'bold',
                            fontSize: '0.7rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 0.5
                          }}>
                            ⏰ {delayInfo.delayMinutes} Min. verspätet
                          </Typography>
                        )}
                        {delayInfo.status === 'delayed' && (
                          <Typography variant="caption" sx={{ 
                            color: '#ff6b00', 
                            fontWeight: 'bold',
                            fontSize: '0.7rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 0.5
                          }}>
                            🕐 {delayInfo.delayMinutes} Min. verspätet gestartet
                          </Typography>
                        )}
                        {delayInfo.status === 'early' && (
                          <Typography variant="caption" sx={{ 
                            color: '#4caf50', 
                            fontWeight: 'bold',
                            fontSize: '0.7rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 0.5
                          }}>
                            ✅ {delayInfo.delayMinutes} Min. früher gestartet
                          </Typography>
                        )}
                        {delayInfo.status === 'ontime' && (
                          <Typography variant="caption" sx={{ 
                            color: '#4caf50', 
                            fontWeight: 'bold',
                            fontSize: '0.7rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 0.5
                          }}>
                            ✅ Pünktlich gestartet
                          </Typography>
                        )}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {renderMedicalTransportInfo(ride)}
                  </TableCell>
                  <TableCell align="center">
                    {formatVehicleWithCapabilities(ride.vehicleType, ride.specialCapabilities || migrateSpecialVehicleType(ride))}
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {/* Erstellt am */}
                      <Typography variant="body2">
                        <strong>Erstellt:</strong> {formatDate(ride.timestamp)}
                      </Typography>
                      
                      {/* Geplante Zeit für Vorbestellungen */}
                      {ride.orderType === 1 && ride.scheduledTime && (
                        <Typography variant="body2" color="primary">
                          <strong>Geplant:</strong> {formatDate(ride.scheduledTime)}
                        </Typography>
                      )}
                      
                      {/* Angenommen am */}
                      {(ride.acceptedTimestamp || ride.acceptedAt) && (
                        <Typography variant="body2" color="success.main">
                          <strong>Angenommen:</strong> {formatDate(ride.acceptedTimestamp || ride.acceptedAt)}
                        </Typography>
                      )}
                      
                      {/* Angekommen am */}
                      {ride.arrivedTimestamp && (
                        <Typography variant="body2">
                          <strong>Angekommen:</strong> {formatDate(ride.arrivedTimestamp)}
                        </Typography>
                      )}
                      
                      {/* Fahrgast abgeholt */}
                      {ride.passengerPickedUpTimestamp && (
                        <Typography variant="body2">
                          <strong>Abgeholt:</strong> {formatDate(ride.passengerPickedUpTimestamp)}
                        </Typography>
                      )}
                      
                      {/* Abgeschlossen am */}
                      {(ride.completedTimestamp || ride.completionTimestamp) && (
                        <Typography variant="body2" color="success.main">
                          <strong>Abgeschlossen:</strong> {formatDate(ride.completedTimestamp || ride.completionTimestamp)}
                        </Typography>
                      )}
                      
                      {/* Geschätzte Fahrzeit */}
                      {ride.estimatedTimeMinutes && (
                        <Typography variant="caption" color="textSecondary">
                          <strong>Schätzung:</strong> {ride.estimatedTimeMinutes} Min.
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    {(() => {
                      const assignedDriverName = ride.driverName || getDriverDisplayName(ride.driver);
                      const assignedDriverNumber =
                        ride.driver?.driverNumber ||
                        (ride.driverId ? driverById[ride.driverId]?.driverNumber : null);
                      const currentOfferDriver = ride.currentOfferDriverId
                        ? driverById[ride.currentOfferDriverId]
                        : null;
                      const currentOfferDriverName =
                        getDriverDisplayName(currentOfferDriver) ||
                        ride.currentOfferDriverName ||
                        (ride.currentOfferDriverId ? `Fahrer ${shortId(ride.currentOfferDriverId)}` : null);
                      const currentOfferDriverNumber = currentOfferDriver?.driverNumber || null;
                      const offerLabel =
                        OFFER_TYPE_LABEL_MAP[ride.currentOfferType] ||
                        DISPATCH_SOURCE_LABEL_MAP[ride.dispatchOfferSource] ||
                        'Angebot läuft';

                      if (ride.driver || ride.driverId) {
                        return (
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Badge
                                color={ride.driver?.status === 'available' ? 'success' :
                                       ride.driver?.status === 'busy' ? 'primary' : 'default'}
                                variant="dot"
                                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                              >
                                <DriverIcon />
                              </Badge>
                              {assignedDriverNumber && (
                                <Chip
                                  label={assignedDriverNumber}
                                  size="small"
                                  color="primary"
                                  variant="filled"
                                  sx={{ height: 20, fontWeight: 700, fontSize: '0.7rem' }}
                                />
                              )}
                            </Box>
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                              {assignedDriverName || 'Unbekannt'}
                            </Typography>
                          </Box>
                        );
                      }

                      if (ride.currentOfferDriverId) {
                        return (
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Badge
                                color="warning"
                                variant="dot"
                                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                              >
                                <DriverIcon color="warning" />
                              </Badge>
                              {currentOfferDriverNumber && (
                                <Chip
                                  label={currentOfferDriverNumber}
                                  size="small"
                                  color="warning"
                                  variant="outlined"
                                  sx={{ height: 20, fontWeight: 700, fontSize: '0.7rem' }}
                                />
                              )}
                            </Box>
                            <Typography variant="caption" color="warning.main" fontWeight="bold">
                              Angefragt
                            </Typography>
                            <Typography variant="body2">
                              {currentOfferDriverName || 'Unbekannt'}
                            </Typography>
                            <Chip
                              size="small"
                              color={ride.currentOfferType === 'auto_follow_up' ? 'info' : 'warning'}
                              variant="outlined"
                              label={offerLabel}
                            />
                            {ride.currentOfferExpiresAt && (
                              <Typography variant="caption" color="textSecondary">
                                bis {formatDate(ride.currentOfferExpiresAt)}
                              </Typography>
                            )}
                          </Box>
                        );
                      }

                      return (
                        <Typography variant="body2" color="textSecondary">
                          Nicht zugewiesen
                        </Typography>
                      );
                    })()}
                  </TableCell>
                  <TableCell align="center">
                    {(() => {
                      const vModel = ride.driverModel || ride.vehicle?.model || vehicleByDriverId[ride.driverId]?.model;
                      const vPlate = ride.driverLicensePlate || ride.vehicle?.licensePlate || vehicleByDriverId[ride.driverId]?.licensePlate;
                      const vNumber = ride.vehicle?.vehicleNumber || vehicleByDriverId[ride.driverId]?.vehicleNumber;
                      return vModel || vPlate ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <CarIcon />
                            {vNumber && (
                              <Chip
                                label={vNumber}
                                size="small"
                                color="primary"
                                variant="filled"
                                sx={{ height: 20, fontWeight: 700, fontSize: '0.7rem' }}
                              />
                            )}
                          </Box>
                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                            {vModel || ''}
                            {vPlate && ` (${vPlate})`}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          Nicht zugewiesen
                        </Typography>
                      );
                    })()}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      aria-label="Aktionen"
                      aria-controls={`ride-menu-${ride.id}`}
                      aria-haspopup="true"
                      onClick={(e) => handleOpenMenu(e, ride)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                    </TableCell>
                  </StyledTableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body1" sx={{ py: 3 }}>
                    Keine Fahrten gefunden
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={filteredData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Zeilen pro Seite:"
          labelDisplayedRows={
            ({ from, to, count }) => `${from}–${to} von ${count}`
          }
        />
      </TableContainer>
      
      {/* Aktions-Menü */}
      <Menu
        id={menuRide ? `ride-menu-${menuRide.id}` : undefined}
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleCloseMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem 
          component={Link} 
          to={menuRide ? `/fahrten/${menuRide.id}` : '#'} 
          onClick={handleCloseMenu}
        >
          Fahrt anzeigen
        </MenuItem>
        {menuRide && canEditRide(menuRide) && menuRide.status !== 'open' && menuRide.status !== 'scheduled' && (
          <MenuItem
            component={Link}
            to={menuRide ? `/fahrten/${menuRide.id}/bearbeiten` : '#'}
            state={{ from: '/fahrten' }}
            onClick={handleCloseMenu}
          >
            <EditIcon fontSize="small" sx={{ mr: 1 }} />
            Bearbeiten
          </MenuItem>
        )}
        {menuRide && (menuRide.status === 'open' || menuRide.status === 'scheduled') && (
          <MenuItem
            component={Link}
            to={menuRide ? `/fahrten/${menuRide.id}/zuweisen` : '#'}
            state={{ from: '/fahrten' }}
            onClick={handleCloseMenu}
          >
            <TaxiIcon fontSize="small" sx={{ mr: 1 }} />
            Fahrer zuweisen
          </MenuItem>
        )}
        {menuRide && [
          'completed', 'cancelled', 'cancelled_by_driver',
          'open', 'dispatcher_pending', 'accepted', 'pre_accepted',
          'arrived', 'in_progress', 'scheduled'
        ].includes(menuRide.status) && (
          <MenuItem
            onClick={() => {
              handleCloseMenu();
              navigate('/fahrten/neu', { state: { duplicateRide: menuRide } });
            }}
          >
            <ContentCopyIcon fontSize="small" sx={{ mr: 1 }} />
            {['completed', 'cancelled', 'cancelled_by_driver'].includes(menuRide.status)
              ? 'Fahrt erneut erstellen'
              : '2. Taxi anfordern'}
          </MenuItem>
        )}
        {canDeleteRides ? (
          <MenuItem
            onClick={() => handleDeleteClick(menuRide)}
            sx={{ color: 'error.main' }}
          >
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
            Löschen
          </MenuItem>
        ) : menuRide && !['completed', 'cancelled', 'cancelled_by_driver'].includes(menuRide.status) ? (
          <MenuItem
            onClick={() => handleCancelRide(menuRide)}
            sx={{ color: 'error.main' }}
          >
            <CancelledIcon fontSize="small" sx={{ mr: 1 }} />
            Stornieren
          </MenuItem>
        ) : null}
      </Menu>

      {/* Lösch-Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Fahrt löschen
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Sind Sie sicher, dass Sie diese Fahrt endgültig löschen möchten? 
            Diese Aktion kann nicht rückgängig gemacht werden.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Abbrechen
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Löschen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RidesList;
